import React, { FC } from 'react';
import { Trans } from '@lingui/react/macro';
import { Modal, Button, Paragraph, Svg, Flex, Card, Heading } from '@wunder/ui-components';
import { Subscription } from '@/features/subscriptions/types/Subscription.types';
import { useRequestBuySubscriptionMutation } from '@/features/subscriptions/services/Subscription.service';
import { getBillingCycleLabel } from '@/features/subscriptions/utils/subscriptionHelpers';
import { useRequestPaymentSourcesQuery } from '@/features/payment/services/Payment.service';
import { useRequestResolvedBranchQuery } from '@/features/branches/services/Branches.service';
import styles from '../../assets/scss/PurchaseConfirmationModal.module.scss';
import CurrencyDisplay from '@/core/components/CurrencyDisplay';

interface PurchaseConfirmationModalProps {
  subscription: Subscription;
  onChangePaymentMethod?: () => void;
  onCancel: () => void;
}

const PurchaseConfirmationModal: FC<PurchaseConfirmationModalProps> = ({
  subscription,
  onChangePaymentMethod,
  onCancel,
}) => {
  const { data: resolvedBranch } = useRequestResolvedBranchQuery();
  const { data: paymentSources } = useRequestPaymentSourcesQuery(
    { branchId: resolvedBranch?.id! },
    { skip: !resolvedBranch?.id },
  );

  const [buySubscription, { isLoading }] = useRequestBuySubscriptionMutation();

  const cycleLabel = getBillingCycleLabel(subscription.interval);
  const defaultPaymentSource = paymentSources?.[0];

  const handlePurchase = async () => {
    try {
      await buySubscription({ subscriptionCode: subscription.code }).unwrap();
      onCancel?.();
    } catch (error) {
      return error;
    }
  };

  const handleChangePaymentMethod = () => {
    onCancel?.();
    onChangePaymentMethod?.();
  };

  return (
    <Modal
      opened
      onClose={onCancel}
      variation="small"
      modalHeader={<Trans id="subscriptions.purchase.title">Confirm Purchase</Trans>}
      modalFooter={
        <Flex direction="column" gap="md" width="full">
          <Paragraph size={5} textColor="text-400" marginBottom={0} align="center">
            <Trans id="subscriptions.purchase.disclaimer">
              By clicking Buy, you agree to the Terms and Conditions. You also agree to a recurring
              charge of {subscription.currency}
              {subscription.price.toFixed(2)} from your default payment method ending in{' '}
              {defaultPaymentSource?.last4 || ''}.
            </Trans>
          </Paragraph>

          <Button
            variation="primary"
            onClick={() => {
              handlePurchase();
            }}
            loading={isLoading}
            disabled={isLoading || !defaultPaymentSource}
            width="full"
            size="large"
          >
            <Trans id="subscriptions.purchase.pay">Pay </Trans>
            <CurrencyDisplay value={subscription.price} currency={subscription.currency} />
            <Trans id="subscriptions.purchase.subscribe"> & Subscribe</Trans>
          </Button>

          <Flex align="center" gap="sm" justify="center">
            <Svg icon="lock" width={16} />
            <Paragraph size={5} marginBottom={0} textColor="text-400">
              <Trans id="subscriptions.purchase.securePayment">Secure payment via Stripe</Trans>
            </Paragraph>
          </Flex>
        </Flex>
      }
    >
      <Flex direction="column" gap="lg">
        <Card className={styles['purchase-confirmation-modal__subscription']}>
          <Flex gap="md" align="flex-start">
            <Svg icon="card" width={32} height={32} />
            <Flex direction="column" width="full">
              <Heading size={6} marginBottom={4}>
                {subscription.description || subscription.code}
              </Heading>
              <Flex align="flex-end">
                <Heading size={5}>
                  <CurrencyDisplay value={subscription.price} currency={subscription.currency} />
                </Heading>
                <Paragraph size={4} textColor="text-400" marginBottom={12}>
                  {cycleLabel}
                </Paragraph>
              </Flex>
              <Paragraph size={5} textColor="text-400" marginBottom={0}>
                <Trans id="subscriptions.purchase.renews">
                  Renews automatically. Cancel anytime.
                </Trans>
              </Paragraph>
            </Flex>
          </Flex>
        </Card>

        <Flex direction="column" gap="sm">
          <Heading size={6}>
            <Trans id="subscriptions.purchase.paymentMethod">Pay with saved card</Trans>
          </Heading>

          <Card className={styles['purchase-confirmation-modal__payment-method']}>
            <Flex align="center" justify="space-between">
              {defaultPaymentSource ? (
                <>
                  <Flex gap="sm" align="center">
                    <Svg
                      icon={(defaultPaymentSource.brand as any) || 'card'}
                      width={60}
                      height={35}
                    />
                    <Paragraph size={4} marginBottom={0}>
                      {defaultPaymentSource.brand || 'Card'} ending in {defaultPaymentSource.last4}
                    </Paragraph>
                  </Flex>
                  <Button variation="link" size="small" onClick={handleChangePaymentMethod}>
                    <Trans id="subscriptions.purchase.changePayment">Change</Trans>
                  </Button>
                </>
              ) : (
                <Paragraph size={4} textColor="text-400" marginBottom={0}>
                  <Trans id="subscriptions.purchase.noPaymentMethod">No payment method saved</Trans>
                </Paragraph>
              )}
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default PurchaseConfirmationModal;
